import { useCallback, useEffect, useRef, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { deleteAvatar, fetchProfile, updateProfile, uploadAvatar, type ProfileDto } from '../../api/profileApi';
import { getApiErrorMessage, publicAssetUrl } from '../../api/http';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

export default function ProfileModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { refreshUser } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ProfileDto | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await fetchProfile();
      setData(p);
      setUsername(p.username || '');
      setEmail(p.email || '');
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to load profile'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!show) return;
    void load();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, [show, load]);

  async function onSave() {
    if (!username.trim()) {
      showToast('Username cannot be empty', 'warning');
      return;
    }
    if (!email.trim()) {
      showToast('Email cannot be empty', 'warning');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('Enter a valid email address', 'warning');
      return;
    }
    if (currentPassword || newPassword || confirmPassword) {
      if (!currentPassword) {
        showToast('Enter your current password to change it', 'warning');
        return;
      }
      if (!newPassword) {
        showToast('Enter a new password', 'warning');
        return;
      }
      if (newPassword.length < 6) {
        showToast('New password must be at least 6 characters', 'warning');
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast('New password and confirmation do not match', 'warning');
        return;
      }
    }

    const payload: Parameters<typeof updateProfile>[0] = {
      username: username.trim(),
      email: email.trim(),
    };
    if (currentPassword && newPassword) {
      payload.current_password = currentPassword;
      payload.new_password = newPassword;
    }

    setSaving(true);
    try {
      const res = await updateProfile(payload);
      if (!res.success) {
        showToast(res.message || 'Profile update failed', 'error');
        return;
      }
      showToast('Profile updated successfully', 'success');
      await refreshUser();
      onClose();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Save failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function onPickFile(f: File | null) {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      showToast('File is too large (max 2 MB)', 'warning');
      return;
    }
    try {
      const r = await uploadAvatar(f);
      if (!r.success || !r.avatar_url) {
        showToast(r.message || 'Could not save photo', 'error');
        return;
      }
      setData((d) => ({ ...d, avatar_url: r.avatar_url }));
      await refreshUser();
      showToast('Profile photo saved', 'success');
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server connection error'), 'error');
    }
  }

  async function onRemoveAvatar() {
    try {
      const r = await deleteAvatar();
      if (!r.success) {
        showToast(r.message || 'Could not remove avatar', 'error');
        return;
      }
      setData((d) => ({ ...d, avatar_url: null }));
      await refreshUser();
      showToast('Avatar removed', 'success');
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server connection error'), 'error');
    }
  }

  const avatarUrl = data?.avatar_url || '';

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      size="lg"
      title="My profile"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving || loading} onClick={() => void onSave()}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="text-center py-4 text-muted">Loading…</div>
      ) : (
        <>
          <div className="rounded-3 border bg-light bg-opacity-50 p-3 p-sm-4 mb-4">
            <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3 gap-sm-4">
              <div className="flex-shrink-0">
                <div
                  className="position-relative rounded-circle overflow-hidden border border-2 border-white shadow-sm bg-white"
                  style={{ width: 112, height: 112 }}
                >
                  {avatarUrl ? (
                    <img src={publicAssetUrl(avatarUrl)} alt="" className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover" />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center w-100 h-100">
                      <i className="fas fa-user fa-2x text-secondary opacity-50" />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-grow-1 text-center text-sm-start w-100" style={{ minWidth: 0 }}>
                <div className="fw-semibold text-body mb-1">Profile photo</div>
                <p className="text-muted small mb-3 lh-sm">JPEG, PNG, WebP or GIF · max 2 MB</p>
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center justify-content-sm-start">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => fileRef.current?.click()}
                  >
                    <i className="fas fa-upload me-1" />
                    Choose photo
                  </button>
                  {avatarUrl ? (
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => void onRemoveAvatar()}>
                      <i className="fas fa-times me-1" />
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            className="d-none"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              void onPickFile(f ?? null);
            }}
          />

          <p className="small fw-semibold text-uppercase text-muted mb-3">Account</p>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">Username</label>
              <input className="form-control" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">Email address</label>
              <input className="form-control" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
          </div>

          <hr className="text-secondary opacity-25 my-4" />

          <p className="small fw-semibold text-uppercase text-muted mb-3">Change password</p>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label small text-muted mb-1">Current password</label>
              <input
                className="form-control"
                type="password"
                autoComplete="current-password"
                placeholder="Leave blank to keep current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">New password</label>
              <input
                className="form-control"
                type="password"
                autoComplete="new-password"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">Confirm new password</label>
              <input
                className="form-control"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <hr className="text-secondary opacity-25 my-4" />

          <p className="small fw-semibold text-uppercase text-muted mb-3">Account information</p>
          <div className="rounded-3 border bg-light bg-opacity-25 p-3">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small text-muted mb-1">Subscription type</label>
                <input className="form-control form-control-sm" readOnly disabled value={(data?.subscription_type || 'Basic').toUpperCase()} />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted mb-1">Valid until</label>
                <input
                  className="form-control form-control-sm"
                  readOnly
                  disabled
                  value={data?.subscription_expiry ? new Date(data.subscription_expiry).toLocaleDateString() : '—'}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted mb-1">Max channels</label>
                <input className="form-control form-control-sm" readOnly disabled value={String(data?.max_channels ?? 1000)} />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted mb-1">Max movies</label>
                <input className="form-control form-control-sm" readOnly disabled value={String(data?.max_movies ?? 1000)} />
              </div>
            </div>
          </div>
        </>
      )}
    </SimpleModal>
  );
}
